import { Button, FileButton, Flex, Group, Select, Text } from '@mantine/core';
import FilesTable from 'src/data-capture/file-management/files-table/FilesTable';
import { useFileTemporaryLinkMutation } from 'src/data-capture/hooks/useFileTemporaryLinkMutation';
import useTemplates, {
  TemplateType,
  UploadedTemplate,
} from 'src/data-capture/hooks/templates/useTemplates';
import PageLoader from 'src/UI/loader/PageLoader';
import { GenericErrorPage } from 'src/error/GenericErrorPage';
import { Company, useCompanies } from 'src/secret-menu/hooks/useCompanies';
import React from 'react';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useCreateTemplateMutation } from 'src/secret-menu/templates/useCreateTemplateMutation';
import { useDeleteTemplateMutation } from 'src/secret-menu/templates/useDeleteTemplateMutation';

export default function DeprecatedTemplateTab() {
  const { companies, isLoading: isLoadingCompanies } = useCompanies();
  return isLoadingCompanies || !companies || companies.length === 0 ? (
    <PageLoader />
  ) : (
    <TemplatesContent companies={companies as [Company, ...Company[]]} />
  );
}

const templateTypes: { label: string; value: TemplateType }[] = [
  {
    label: 'Suitability Report',
    value: 'SUITABILITY_REPORT',
  },
  {
    label: 'Meeting Agenda',
    value: 'MEETING_AGENDA',
  },
  {
    label: 'Client Information Document',
    value: 'CLIENT_INFORMATION_DOCUMENT',
  },
  {
    label: 'Meeting Note',
    value: 'MEETING_NOTE',
  },
];

function TemplatesContent({ companies }: { companies: [Company, ...Company[]] }) {
  const [selectedCompany, setSelectedCompany] = React.useState<Company>(companies[0]);
  const [selectedTemplateType, setSelectedTemplateType] = React.useState<TemplateType>(
    templateTypes[0].value,
  );
  const { createTemplate, isLoading: isCreatingTemplate } = useCreateTemplateMutation();

  const {
    deleteTemplate,
    isLoading: isDeleting,
    isError: deletionFailed,
  } = useDeleteTemplateMutation();

  const {
    createLink,
    isLoading: isCreatingLink,
    isError: creatingLinkFailed,
  } = useFileTemporaryLinkMutation();

  const {
    files: unfilteredFiles,
    isLoading: isLoadingFiles,
    refetch: refetchFiles,
    isError: failedToLoadFiles,
  } = useTemplates({
    companyId: selectedCompany.id,
    templateType: selectedTemplateType,
  });

  const handleViewFile = async (template: UploadedTemplate) => {
    const fileId = template.file?.id;
    if (!fileId) {
      notifyAndLogError(
        'File not available',
        'The selected template does not have an associated file to view.',
        new Error('File ID is missing for the selected template'),
      );
      return;
    }
    const link = await createLink(fileId);
    window.open(link.url, '_blank');
  };

  const handleDeleteFile = async (template: UploadedTemplate) => {
    try {
      await deleteTemplate(selectedCompany.id, template.id);
      await refetchFiles();
    } catch (error) {
      notifyAndLogError(
        'Failed to delete template',
        'An error occurred while deleting the template. Please try again.',
        error,
      );
    }
  };

  if (isCreatingTemplate || isLoadingFiles || isDeleting || isCreatingLink) {
    return <PageLoader absolute={false} />;
  }

  if (failedToLoadFiles || deletionFailed || creatingLinkFailed) {
    return (
      <GenericErrorPage
        error={{
          failedToLoadFiles,
          deletionFailed,
          creatingLinkFailed,
        }}
      />
    );
  }

  const handleFileSelection = async (file: File | null) => {
    try {
      if (!file) {
        notifyAndLogError(
          'No file selected',
          'Please select a file to create a template.',
          new Error('No file selected'),
        );
        return;
      }
      await createTemplate({
        file,
        companyId: selectedCompany.id,
        templateType: selectedTemplateType,
      });
      await refetchFiles();
    } catch (error) {
      notifyAndLogError(
        'Failed to create template',
        'An error occurred while creating the template. Please try again.',
        error,
      );
    }
  };

  return (
    <Flex direction="column" gap="xxl" mt="xs">
      <Flex align={'flex-end'} justify={'space-between'}>
        <Text size="xxl" fw={600}>
          Templates
        </Text>

        <Group align={'flex-end'}>
          <Select
            label={'Template type'}
            data={templateTypes}
            value={selectedTemplateType}
            onChange={value =>
              setSelectedTemplateType((value || templateTypes[0].value) as TemplateType)
            }
            clearable={false}
            w={250}
          />
          <Select
            label={'Company'}
            data={companies?.map(company => ({ label: company.name, value: company.id })) || []}
            value={selectedCompany.id}
            onChange={value =>
              setSelectedCompany(companies.find(c => c.id === value) || companies[0])
            }
            w={500}
          />
          <FileButton onChange={handleFileSelection}>
            {props => <Button {...props}>Create template</Button>}
          </FileButton>
        </Group>
      </Flex>

      {/* @ts-expect-error temporary */}
      <FilesTable<UploadedTemplate>
        entitiesWithFile={unfilteredFiles.filter(f => !!f.file)}
        isLoading={isLoadingFiles}
        actions={[
          { label: 'View', action: handleViewFile },
          {
            label: 'Delete',
            action: handleDeleteFile,
          },
        ]}
      />
    </Flex>
  );
}
